import { GameState, SlotCard } from '../types/types';
import { CARD_TYPE_NAMES } from './cardConstants';

/**
 * Checks for 3 matching cards in the slots and removes them if found
 * @param gameState - Current game state
 * @param setAnimationInProgress - Function to set animation progress state
 * @param setGameState - Function to update game state
 * @returns {boolean} - Whether a match was found
 */
export const checkForMatches = (
  gameState: GameState,
  setAnimationInProgress: (value: boolean) => void,
  setGameState: (state: GameState | ((prevState: GameState) => GameState)) => void,
  animationInProgress: boolean
): boolean => {
  // If an animation is already in progress, wait for it to complete
  if (animationInProgress) {
    console.log('Animation in progress, delaying match check');
    return false;
  }
  
  const { slots } = gameState;
  
  if (slots.length < 3) {
    return false; // Not enough cards to match
  }
  
  // Print exact slot contents for debugging
  console.log('SLOT CONTENT:', slots.map(s => `${s.type} (${s.id})`).join(', '));
  
  // Create a simple type counter by type key
  const typeCounts: Record<string, number> = {};
  
  // Count each card type - making sure to convert to string keys
  slots.forEach(card => {
    const typeKey = String(card.type);
    typeCounts[typeKey] = (typeCounts[typeKey] || 0) + 1;
    console.log(`Card of type ${typeKey} (${CARD_TYPE_NAMES[card.type] || 'unknown'}) count now: ${typeCounts[typeKey]}`);
  });

  console.log('TYPE COUNTS:', JSON.stringify(typeCounts));
  
  // Check if any type has 3 or more cards
  let matchingTypeKey: string | null = null;
  
  // Find any type with at least 3 cards
  for (const [typeKey, count] of Object.entries(typeCounts)) {
    if (count >= 3) {
      matchingTypeKey = typeKey;
      const typeNum = parseInt(typeKey);
      console.log(`MATCH FOUND: ${count} cards of type ${typeKey} (${CARD_TYPE_NAMES[typeNum] || 'unknown'})`);
      break; // Stop at first match found
    }
  }
  
  // If we found a match, remove those cards
  if (matchingTypeKey !== null) {
    const matchingType = parseInt(matchingTypeKey);
    
    // Set animation in progress flag
    setAnimationInProgress(true);
    
    // First, identify which indices contain the matching cards to remove
    const indicesToRemove: number[] = [];
    let cardsOfMatchingType = 0;
    
    slots.forEach((card, index) => {
      if (card.type === matchingType && cardsOfMatchingType < 3) {
        indicesToRemove.push(index);
        cardsOfMatchingType++;
        const typeName = CARD_TYPE_NAMES[card.type] || 'unknown';
        console.log(`Marking card at index ${index} with ID ${card.id} of type ${card.type} (${typeName}) for removal`);
      }
    });
    
    // Clear any existing animations first to avoid interference
    document.querySelectorAll('.match-animation').forEach(element => {
      element.classList.remove('match-animation');
    });
    
    // Store the IDs of cards to be removed for precise targeting
    const cardIdsToRemove = indicesToRemove.map(index => slots[index].id);
    console.log('Card IDs to remove:', cardIdsToRemove);
    
    // Use requestAnimationFrame to apply animations at the optimal time
    requestAnimationFrame(() => {
      // Only apply animation to cards with specific IDs we're removing
      const slotCardElements = document.querySelectorAll('.slot-card');
      slotCardElements.forEach(cardElement => {
        const cardId = cardElement.getAttribute('data-card-id');
        if (cardId && cardIdsToRemove.includes(cardId)) {
          console.log(`Adding animation to card with ID ${cardId}`);
          cardElement.classList.add('match-animation');
        }
      });
    });
    
    // Create a new array without the matched cards, maintaining order
    const newSlots: SlotCard[] = [];
    for (let i = 0; i < slots.length; i++) {
      if (!indicesToRemove.includes(i)) {
        newSlots.push(slots[i]);
      }
    }
    
    console.log(`Removed ${cardsOfMatchingType} cards at indices ${indicesToRemove.join(', ')}, ${newSlots.length} remaining`);
    console.log('New slot contents:', newSlots.map(s => `${s.type} (${s.id})`).join(', '));
    
    // Wait for animation to finish before updating state
    setTimeout(() => {
      // Make sure all match animations are removed before state update
      document.querySelectorAll('.match-animation').forEach(element => {
        element.classList.remove('match-animation');
      });
      
      // Create a completely new state object with the updated slots
      const updatedState: GameState = {
        ...gameState,
        slots: [...newSlots]
      };
      
      // Force immediate update with the new state
      setGameState(updatedState);
      
      // Clear animation in progress flag
      setAnimationInProgress(false);
      
      // Log the new state for debugging
      console.log('Updated state after match:', updatedState.slots.map(s => `${s.type} (${s.id})`).join(', '));
    }, 300); // Match the animation duration
    
    return true; // Return true to indicate we found and removed a match
  }
  
  return false; // No match found
}; 